import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import DescriptionIcon from '@material-ui/icons/Description'
import { Fragment, useState } from 'react'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Button } from 'shared/components/Button/Button'
import { Text } from 'shared/components/Text/Text'
import { Handler } from 'shared/helpers/typeHelper'

import { useBreakpoint } from '../../../hooks/useBreakpoint'
import {
  createPolicyDocument,
  deletePolicyDocument,
  PolicyDocument,
  Repayment,
} from '../../../models/repayments'
import { bgGray, lightGray1, midGray, primaryBlack, royalBlue } from '../../../theme/colors'
import { Grid } from '../../Base/Grid'
import { Wrap } from '../../Base/Wrap'
import { Box } from '../../Box/Box'
import { SimpleDialog } from '../../Dialog/SimpleDialog'
import { DropZoneArea } from '../../DropZoneArea/DropZoneArea'
import { ExternalLink } from '../../ExternalLink/ExternalLink'
import { StyledIcon } from '../../Icons/StyledIcon'

type Props = {
  policy?: Repayment
  policyDocuments?: PolicyDocument[]
  refetchDocuments?: Handler
  draft?: boolean
  draftDocuments?: File[]
  setDraftDocuments?: (documents: File[]) => void
}

export function PolicyDocuments({
  policy,
  policyDocuments,
  refetchDocuments,
  draft,
  draftDocuments,
  setDraftDocuments,
}: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const [file, setFile] = useState<File | null | undefined>(null)
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false)
  const documents = policyDocuments ?? draftDocuments ?? []
  const isMobile = useBreakpoint('xs')

  async function handleUpload() {
    if (!file) return
    setIsLoading(true)

    if (policy && !draft) {
      await createPolicyDocument(policy?.repayment_details.policy_id, file).then(refetchDocuments)
    } else {
      setDraftDocuments?.([...(draftDocuments ?? []), file])
    }

    setIsLoading(false)
    setIsUploadDialogOpen(false)
  }

  return (
    <FlexBox flex={1} isColumn justifyContent="flex-start" alignItems="flex-start">
      <Text variant="uppercaseLabel2" color={midGray} mb={2}>
        Documents {draft && '(Optional)'}
      </Text>
      {documents.length === 0 && !draft ? (
        <Text variant="header3" color={lightGray1}>
          No documents
        </Text>
      ) : (
        <Grid
          columns="auto 1fr auto"
          alignItems="center"
          gap="10px"
          styles={`width: ${isMobile ? '100%' : '400px'}; max-width: 100%;`}
        >
          {(policyDocuments ?? []).map((item) => (
            <Fragment key={item.id}>
              <ExternalLink href={item.policy_document_file} target="_blank">
                <DescriptionIcon color="primary" />
              </ExternalLink>
              <ExternalLink href={item.policy_document_file} target="_blank">
                <Text variant="header3" color={primaryBlack}>
                  {item.readable_filename ?? 'Policy document'}
                </Text>
              </ExternalLink>
              <Button
                variant="text"
                onClick={() => deletePolicyDocument(item.id).then(() => refetchDocuments?.())}
              >
                Delete
              </Button>
            </Fragment>
          ))}

          {(draftDocuments ?? []).map((item, idx) => (
            <Fragment key={item.name + idx}>
              <DescriptionIcon color="primary" />
              <FlexBox style={{ marginRight: 100 }}>
                <Text variant="header3" color={primaryBlack}>
                  {item.name ?? 'Policy document'}
                </Text>
              </FlexBox>
              <Button
                variant="text"
                isInline
                onClick={() =>
                  setDraftDocuments?.([
                    ...(draftDocuments ?? []).filter((d, _idx) => !(d.name === item.name && _idx === idx)),
                  ])
                }
              >
                Delete
              </Button>
            </Fragment>
          ))}
        </Grid>
      )}

      <Box mt={3} />
      <SimpleDialog isOpen={isUploadDialogOpen} onClose={() => setIsUploadDialogOpen(false)}>
        <Wrap width="400px" maxWidth="100%">
          <Text variant="header2" color={primaryBlack} mb={2} align="left">
            Upload document
          </Text>
          <DropZoneArea onChange={setFile} backgroundColor={bgGray} />
          <Box mt={4} />
          <Button
            variant="primary"
            margin="0 auto"
            onClick={handleUpload}
            isDisabled={!file}
            isLoading={isLoading}
          >
            Upload
          </Button>
          <Box mt={3} />
          <Button variant="text" margin="0 auto" onClick={() => setIsUploadDialogOpen(false)}>
            Cancel
          </Button>
        </Wrap>
      </SimpleDialog>
      <Button
        variant="text"
        onClick={() => setIsUploadDialogOpen(true)}
        icon={<StyledIcon icon={<AddCircleOutlineIcon />} color={royalBlue} />}
      >
        Add
      </Button>
    </FlexBox>
  )
}
